import { useMemo } from "react";
import {
  CARE_MANAGER_EXPERIENCE_OPTIONS,
  CARE_MANAGER_LOCALITIES,
  CARE_MANAGER_SERVICE_OPTIONS,
  CARE_MANAGER_SIGNUP_CITIES,
  CARE_MANAGER_SIGNUP_TERMS_EXCERPT,
  CARE_MANAGER_TYPE_OPTIONS,
} from "../../config/careManagerRegistration";
import { normalizeCmPin } from "../../utils/careManagerSignupValidation";
import { OTP_LENGTH, sanitizeOtpInput } from "../../utils/otpCode";

export default function CareManagerSignupFields({
  step,
  createValues,
  createErrors,
  onCreateFieldChange,
  managerForm,
  onManagerPatch,
  customArea,
  onCustomAreaChange,
  onAddCustomArea,
  signupOtpSent,
  otpCode,
  onOtpChange,
  otpDisabled,
  termsError,
  onTermsAcceptedChange,
}) {
  const localities = useMemo(
    () => CARE_MANAGER_LOCALITIES[managerForm.city] || [],
    [managerForm.city]
  );

  function toggleService(service) {
    const set = new Set(managerForm.servicesOffered || []);
    if (set.has(service)) set.delete(service);
    else set.add(service);
    onManagerPatch({ servicesOffered: [...set] });
  }

  function toggleArea(area) {
    const set = new Set(managerForm.serviceAreas || []);
    if (set.has(area)) set.delete(area);
    else set.add(area);
    onManagerPatch({ serviceAreas: [...set] });
  }

  if (step === 1) {
    return (
      <>
        <div className="name-form">
          <div className="login-form__field">
            <label className="login-form__label" htmlFor="cm-create-first-name">
              First name <span className="required-star">*</span>
            </label>
            <input
              id="cm-create-first-name"
              name="firstName"
              type="text"
              value={createValues.firstName}
              onChange={onCreateFieldChange}
              className={`login-form__input${createErrors.firstName ? " login-form__input--error" : ""}`}
              placeholder="Priya"
            />
            {createErrors.firstName ? (
              <p className="login-form__field-error">{createErrors.firstName}</p>
            ) : null}
          </div>
          <div className="login-form__field">
            <label className="login-form__label" htmlFor="cm-create-last-name">
              Last name
            </label>
            <input
              id="cm-create-last-name"
              name="lastName"
              type="text"
              value={createValues.lastName}
              onChange={onCreateFieldChange}
              className={`login-form__input${createErrors.lastName ? " login-form__input--error" : ""}`}
              placeholder="Reddy"
            />
            {createErrors.lastName ? (
              <p className="login-form__field-error">{createErrors.lastName}</p>
            ) : null}
          </div>
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-create-mobile">
            Mobile number <span className="required-star">*</span>
          </label>
          <input
            id="cm-create-mobile"
            name="mobile"
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={createValues.mobile}
            onChange={onCreateFieldChange}
            className={`login-form__input${createErrors.mobile ? " login-form__input--error" : ""}`}
            placeholder="10-digit mobile (used to sign in)"
          />
          {createErrors.mobile ? <p className="login-form__field-error">{createErrors.mobile}</p> : null}
        </div>
        <p className="auth-field__hint">
          We verify your mobile with OTP when you submit the application on the final step.
        </p>
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-manager-type">
            Care manager type <span className="required-star">*</span>
          </label>
          <select
            id="cm-manager-type"
            className="login-form__input auth-select"
            value={managerForm.managerType}
            onChange={(e) => onManagerPatch({ managerType: e.target.value })}
          >
            {CARE_MANAGER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-experience">
            Years of experience <span className="required-star">*</span>
          </label>
          <select
            id="cm-experience"
            className="login-form__input auth-select"
            value={managerForm.experienceYears}
            onChange={(e) => onManagerPatch({ experienceYears: e.target.value })}
          >
            {CARE_MANAGER_EXPERIENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-qualification">
            Professional qualification <span className="required-star">*</span>
          </label>
          <input
            id="cm-qualification"
            className="login-form__input"
            value={managerForm.qualificationSummary}
            onChange={(e) => onManagerPatch({ qualificationSummary: e.target.value })}
            placeholder="e.g. M.Sc. in Gerontology / GNM Nursing Degree"
          />
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-employment">
            Current employment status <span className="required-star">*</span>
          </label>
          <input
            id="cm-employment"
            className="login-form__input"
            value={managerForm.employmentStatus}
            onChange={(e) => onManagerPatch({ employmentStatus: e.target.value })}
            placeholder="e.g. Lead Nurse at Fortis Healthcare / Private Consultant"
          />
        </div>
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-city">
            Primary city <span className="required-star">*</span>
          </label>
          <select
            id="cm-city"
            className="login-form__input auth-select"
            value={managerForm.city}
            onChange={(e) => {
              const city = e.target.value;
              const meta = CARE_MANAGER_SIGNUP_CITIES.find((c) => c.name === city);
              onManagerPatch({ city, state: meta?.state || "", serviceAreas: [] });
            }}
          >
            {CARE_MANAGER_SIGNUP_CITIES.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-state">
            State
          </label>
          <input id="cm-state" className="login-form__input login-form__input--readonly" value={managerForm.state} readOnly />
        </div>
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-pin">
            PIN code <span className="required-star">*</span>
          </label>
          <input
            id="cm-pin"
            inputMode="numeric"
            className="login-form__input"
            value={managerForm.pinCode}
            onChange={(e) => onManagerPatch({ pinCode: normalizeCmPin(e.target.value) })}
            placeholder="e.g. 560001"
          />
        </div>
        <div className="login-form__field">
          <span className="login-form__label">
            Areas served in {managerForm.city} <span className="required-star">*</span>
          </span>
          <div className="auth-cm-chips">
            {localities.map((loc) => {
              const active = managerForm.serviceAreas.includes(loc);
              return (
                <button
                  key={loc}
                  type="button"
                  className={`auth-cm-chip${active ? " auth-cm-chip--active" : ""}`}
                  onClick={() => toggleArea(loc)}
                >
                  {active ? "✓" : "+"} {loc}
                </button>
              );
            })}
          </div>
          <div className="auth-cm-custom-area">
            <input
              className="login-form__input"
              value={customArea}
              onChange={(e) => onCustomAreaChange(e.target.value)}
              placeholder="Or type a custom sector/neighborhood and hit Enter"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onAddCustomArea();
                }
              }}
            />
            <button type="button" className="login-form__otp-btn auth-cm-add-btn" onClick={onAddCustomArea}>
              Add
            </button>
          </div>
        </div>
      </>
    );
  }

  if (step === 4) {
    return (
      <>
        <p className="auth-field__hint">
          Select the care specializations you are qualified to structure plans, audit, and dispatch nurses for.
        </p>
        <div className="auth-cm-service-grid">
          {CARE_MANAGER_SERVICE_OPTIONS.map((service) => (
            <label key={service} className="auth-cm-service-card">
              <input
                type="checkbox"
                checked={managerForm.servicesOffered.includes(service)}
                onChange={() => toggleService(service)}
              />
              <span>{service}</span>
            </label>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="auth-cm-terms-scroll">{CARE_MANAGER_SIGNUP_TERMS_EXCERPT}</div>
      <label className="terms-row auth-cm-terms-check">
        <input
          type="checkbox"
          checked={managerForm.termsAccepted}
          onChange={(e) => onTermsAcceptedChange(e.target.checked)}
        />
        <span>
          I agree to NEMA CareHub&apos;s Terms and Conditions, Clinical Quality guidelines, and Privacy Policies. I
          certify my qualifications are true.
        </span>
      </label>
      {termsError ? <p className="login-form__field-error">{termsError}</p> : null}
      <div className="auth-cm-info">
        <strong>Secure account credentials instantly</strong>
        <p>
          When you submit, we verify your mobile with OTP and create your dispatcher credentials. You can then log in
          and track live KYC compliance progress.
        </p>
      </div>
      {signupOtpSent ? (
        <div className="login-form__field">
          <label className="login-form__label" htmlFor="cm-signup-otp">
            Verification code
          </label>
          <input
            id="cm-signup-otp"
            className="login-form__input"
            inputMode="numeric"
            maxLength={OTP_LENGTH}
            value={otpCode}
            onChange={(e) => onOtpChange(sanitizeOtpInput(e.target.value))}
            disabled={otpDisabled}
          />
        </div>
      ) : null}
    </>
  );
}
